"use client";
import { useState } from 'react';
import Link from 'next/link';
import { ThemeToggle } from '@/components/ThemeToggle';
import { AnimatedBackground } from '@/components/AnimatedBackground';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    
    if (!email) {
      setError('Vänligen ange din e-postadress');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Kunde inte skicka återställningslänk');
      }
      
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Kunde inte skicka återställningslänk');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <main className="min-h-screen flex flex-col items-center justify-center px-6 py-20 relative overflow-x-hidden">
      <AnimatedBackground />
      <div className="absolute top-4 right-4 z-20">
        <ThemeToggle />
      </div>
      
      <div className="relative z-10 w-full max-w-md">
        <div className="card-base">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Glömt lösenord?</h1>
          <p className="text-slate-600 dark:text-slate-400 mb-6">
            Ange din e-postadress så skickar vi en länk för att återställa ditt lösenord.
          </p>
          
          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}
          
          {success && (
            <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <p className="text-green-700 dark:text-green-400 text-sm">
                Om ett konto finns med denna e-postadress har en återställningslänk skickats. Kontrollera din inkorg.
              </p>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                E-postadress
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input-base w-full"
                placeholder="din@epost.se"
                disabled={loading || success}
              />
            </div>
            
            <button
              type="submit"
              disabled={loading || success}
              className="btn-primary w-full"
            >
              {loading ? 'Skickar...' : success ? 'E-post skickad!' : 'Skicka återställningslänk'}
            </button>
          </form>
          
          <div className="mt-6 text-center space-y-2">
            <div>
              <Link href="/login" className="text-sm text-primary-600 dark:text-primary-400 hover:underline">
                Tillbaka till inloggning
              </Link>
            </div>
            <div>
              <Link href="/signup" className="text-sm text-slate-600 dark:text-slate-400 hover:underline">
                Har du inget konto? Registrera dig
              </Link>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

